import { zodResolver } from '@hookform/resolvers/zod';
import { ConfirmationDialog } from '@optools/components/dialog/confirmation-dialog';
import { Button } from '@optools/components/ui/button';
import { Form } from '@optools/components/ui/form';
import { PipelineConfig } from '@optools/services/operator-gateway/pipeline-config/types';
import { Cpu, Globe, Handshake, HardDriveUpload, MapPin, Rss } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { PipelineConfigFormSection } from './pipeline-config-form-section';

interface PipelineConfigField {
  description?: string;
  disabled?: boolean;
  label: string;
  section: 'decoder' | 'source' | 'url' | 'destination' | 'partner' | 'red5';
  type: 'text' | 'number' | 'switch';
}

type PipelineConfigKey = keyof PipelineConfig;

/* eslint-disable perfectionist/sort-objects */
export const PipelineConfigFieldMapping: { [key in PipelineConfigKey]: PipelineConfigField } = {
  activeSource: {
    disabled: true,
    label: 'Active Source',
    section: 'source',
    type: 'text',
  },
  altaChannelUrl: {
    label: 'Alta Channel URL',
    section: 'url',
    type: 'text',
  },
  backhaulDecoderHost: {
    label: 'Backhaul Decoder Host',
    section: 'decoder',
    type: 'text',
  },
  backhaulDecoderServiceLabel: {
    label: 'Backhaul Decoder Service Label',
    section: 'decoder',
    type: 'text',
  },
  eviProgramSwitchSrc: {
    label: 'EVI Program Switch Source',
    section: 'source',
    type: 'number',
  },
  eviSdpUrlAudio: {
    label: 'EVI SDP URL Audio',
    section: 'url',
    type: 'text',
  },
  eviSdpUrlVideo: {
    label: 'EVI SDP URL Video',
    section: 'url',
    type: 'text',
  },
  eviSwitchDst: {
    label: 'EVI Switch Destination',
    section: 'destination',
    type: 'number',
  },
  fronthaulDecoderHost: {
    label: 'Fronthaul Decoder Host',
    section: 'decoder',
    type: 'text',
  },
  fronthaulDecoderServiceLabel: {
    label: 'Fronthaul Decoder Service Label',
    section: 'decoder',
    type: 'text',
  },
  lexiInstanceUrl: {
    label: 'Lexi Instance URL',
    section: 'url',
    type: 'text',
  },
  partnerLockEnabled: {
    label: 'Partner Lock Orchestration',
    section: 'partner',
    type: 'switch',
  },
  partnerLockExclusions: {
    description:
      'Enter a comma-separated list of source types to exclude from Orchestration.\n Example 1: PD01,PD01 - All sources tagged PD01 or PD02 will be excluded\n Example 2: NJ - All sources tagged NJ will be excluded',
    label: 'Partner Lock Exclusions',
    section: 'partner',
    type: 'text',
  },
  pd01Dst: {
    label: 'PD 01 Destination',
    section: 'destination',
    type: 'number',
  },
  pd02Dst: {
    label: 'PD 02 Destination',
    section: 'destination',
    type: 'number',
  },
  pd03Dst: {
    label: 'PD 03 Destination',
    section: 'destination',
    type: 'number',
  },
  postGameSrc: {
    label: 'Post-Game Source',
    section: 'source',
    type: 'number',
  },
  preGameSrc: {
    label: 'Pre-Game Source',
    section: 'source',
    type: 'number',
  },
  primaryBackhaulSrc: {
    label: 'Primary Backhaul Source',
    section: 'source',
    type: 'number',
  },
  primaryFronthaulSrc: {
    label: 'Primary Fronthaul Source',
    section: 'source',
    type: 'number',
  },
  programSwitchDst: {
    label: 'Program Switch Destination',
    section: 'destination',
    type: 'number',
  },
  programSwitchSrc: {
    label: 'Program Switch Source',
    section: 'source',
    type: 'number',
  },
  red5PrimaryHost: {
    label: 'Red5 Primary Host',
    section: 'red5',
    type: 'text',
  },
  red5BackupHost: {
    label: 'Red5 Backup Host',
    section: 'red5',
    type: 'text',
  },
  red5PrimaryNodeGroup: {
    label: 'Red5 Primary Node Group',
    section: 'red5',
    type: 'text',
  },
  red5BackupNodeGroup: {
    label: 'Red5 Backup Node Group',
    section: 'red5',
    type: 'text',
  },
  red5PreProcStreamName: {
    label: 'Red5 Pre-Proc Stream Name',
    section: 'red5',
    type: 'text',
  },
  red5PostProcStreamName: {
    label: 'Red5 Post-Proc Stream Name',
    section: 'red5',
    type: 'text',
  },
  sdpUrlAncillary: {
    label: 'SDP URL Ancillary',
    section: 'url',
    type: 'text',
  },
  sdpUrlAudio: {
    label: 'SDP URL Audio',
    section: 'url',
    type: 'text',
  },
  sdpUrlVideo: {
    label: 'SDP URL Video',
    section: 'url',
    type: 'text',
  },
  videonPrimaryStreamUrl: {
    label: 'Videon Primary Stream URL',
    section: 'url',
    type: 'text',
  },
  videonBackupStreamUrl: {
    label: 'Videon Backup Stream URL',
    section: 'url',
    type: 'text',
  },
};
/* eslint-enable perfectionist/sort-objects */

export const defaultValues: { [key in PipelineConfigKey]: PipelineConfig[PipelineConfigKey] } = {
  activeSource: '',
  altaChannelUrl: '',
  backhaulDecoderHost: '',
  backhaulDecoderServiceLabel: '',
  eviProgramSwitchSrc: '',
  eviSdpUrlAudio: '',
  eviSdpUrlVideo: '',
  eviSwitchDst: '',
  fronthaulDecoderHost: '',
  fronthaulDecoderServiceLabel: '',
  lexiInstanceUrl: '',
  partnerLockEnabled: false,
  partnerLockExclusions: [],
  pd01Dst: '',
  pd02Dst: '',
  pd03Dst: '',
  postGameSrc: '',
  preGameSrc: '',
  primaryBackhaulSrc: '',
  primaryFronthaulSrc: '',
  programSwitchDst: '',
  programSwitchSrc: '',
  red5BackupHost: '',
  red5BackupNodeGroup: '',
  red5PostProcStreamName: '',
  red5PreProcStreamName: '',
  red5PrimaryHost: '',
  red5PrimaryNodeGroup: '',
  sdpUrlAncillary: '',
  sdpUrlAudio: '',
  sdpUrlVideo: '',
  videonBackupStreamUrl: '',
  videonPrimaryStreamUrl: '',
} as const;

// This just anyfies it, we need to find a better approach
export const formSchema = z.object<{
  [key in PipelineConfigKey]: z.ZodAny | z.ZodType<string[], string>;
}>({
  activeSource: z.any(),
  altaChannelUrl: z.any(),
  backhaulDecoderHost: z.any(),
  backhaulDecoderServiceLabel: z.any(),
  eviProgramSwitchSrc: z.any(),
  eviSdpUrlAudio: z.any(),
  eviSdpUrlVideo: z.any(),
  eviSwitchDst: z.any(),
  fronthaulDecoderHost: z.any(),
  fronthaulDecoderServiceLabel: z.any(),
  lexiInstanceUrl: z.any(),
  partnerLockEnabled: z.any(),
  partnerLockExclusions: z
    .string()
    .transform((val) => (val?.length ? val.split(',').filter(Boolean) : [])),
  pd01Dst: z.any(),
  pd02Dst: z.any(),
  pd03Dst: z.any(),
  postGameSrc: z.any(),
  preGameSrc: z.any(),
  primaryBackhaulSrc: z.any(),
  primaryFronthaulSrc: z.any(),
  programSwitchDst: z.any(),
  programSwitchSrc: z.any(),
  red5BackupHost: z.any(),
  red5BackupNodeGroup: z.any(),
  red5PostProcStreamName: z.any(),
  red5PreProcStreamName: z.any(),
  red5PrimaryHost: z.any(),
  red5PrimaryNodeGroup: z.any(),
  sdpUrlAncillary: z.any(),
  sdpUrlAudio: z.any(),
  sdpUrlVideo: z.any(),
  videonBackupStreamUrl: z.any(),
  videonPrimaryStreamUrl: z.any(),
});

export type PipelineConfigFormValues = z.infer<typeof formSchema>;

export interface PipelineConfigFormProps {
  config: PipelineConfig;
  editMode: boolean;
  loading: boolean;
  onCancel: () => void;
  onSubmit: (values: PipelineConfigFormValues) => void;
}

export function PipelineConfigForm({
  config,
  editMode,
  loading,
  onCancel,
  onSubmit,
}: PipelineConfigFormProps) {
  const defaultFormValues = Object.fromEntries(
    Object.entries(defaultValues).map(([key, value]) => {
      const configValue = config[key as PipelineConfigKey] ?? value;
      const transformedValue = Array.isArray(configValue) ? configValue.join(',') : configValue;
      return [key, transformedValue];
    }),
  );

  const form = useForm<PipelineConfigFormValues>({
    defaultValues: defaultFormValues,
    resolver: zodResolver(formSchema),
  });

  function handleSubmit(values: PipelineConfigFormValues) {
    onSubmit(values);
  }

  function handleCancel() {
    form.reset();
    onCancel();
  }

  const { decoder, destination, partner, red5, source, url } = Object.entries(
    PipelineConfigFieldMapping,
  ).reduce<{
    [key in PipelineConfigField['section']]: PipelineConfigKey[];
  }>(
    (acc, [key, { section }]) => {
      acc[section].push(key as PipelineConfigKey);
      return acc;
    },

    { decoder: [], destination: [], partner: [], red5: [], source: [], url: [] },
  );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <div className="space-y-16">
          <PipelineConfigFormSection
            editMode={editMode}
            fields={decoder}
            form={form}
            icon={<Cpu className="size-5" />}
            title="Decoder"
          />

          <PipelineConfigFormSection
            editMode={editMode}
            fields={source}
            form={form}
            icon={<HardDriveUpload className="size-5" />}
            title="Source"
          />

          <PipelineConfigFormSection
            editMode={editMode}
            fields={destination}
            form={form}
            icon={<MapPin className="size-5" />}
            title="Destination"
          />

          <PipelineConfigFormSection
            editMode={editMode}
            fields={url}
            form={form}
            icon={<Globe className="size-5" />}
            title="URL"
          />

          <PipelineConfigFormSection
            editMode={editMode}
            fields={red5}
            form={form}
            icon={<Rss className="size-5" />}
            title="Red5"
          />

          <PipelineConfigFormSection
            editMode={editMode}
            fields={partner}
            form={form}
            icon={<Handshake className="size-5" />}
            title="Partner"
          />
        </div>

        {editMode && (
          <div className="mt-12 flex justify-end gap-2">
            <Button
              className="w-32"
              disabled={loading}
              type="submit"
              variant="secondary"
              onClick={handleCancel}>
              Cancel
            </Button>

            <ConfirmationDialog
              description="Are you sure you want to update the pipeline config?"
              title="Update Pipeline Config"
              onConfirm={() => form.handleSubmit(handleSubmit)()}>
              <Button aria-label="Save" className="w-32" disabled={loading} loading={loading}>
                Save
              </Button>
            </ConfirmationDialog>
          </div>
        )}
      </form>
    </Form>
  );
}
